import Dexie, { Table } from 'dexie';
import { IndexedDbWindow } from './indexed-db-window';
import { GetPushEventsParams, PushEvent } from './push-event';
import { TourPlanLoadFilter } from './tour-plan-load-filter';

export class DexieIndexedDb extends Dexie {
  private pushEvents!: Table<PushEvent>; // initialized with `stores` call in ctor
  private tourPlanLoadFilters!: Table<TourPlanLoadFilter>;

  private constructor() {
    super('cargonexx-local-datastore');

    this.version(1).stores({
      pushEvents: '[id+timestamp], type',
      tourPlanLoadFilters: '[depotId+date], updatedAt',
    });
  }

  static getInstance(): DexieIndexedDb {
    const indexedDbWindow = window as unknown as IndexedDbWindow;

    if (indexedDbWindow.dexieIndexedDb == undefined) {
      indexedDbWindow.dexieIndexedDb = new DexieIndexedDb();
    }

    return indexedDbWindow.dexieIndexedDb;
  }

  async getAllPushEvents(): Promise<PushEvent[]> {
    return this.pushEvents.reverse().sortBy('timestamp');
  }

  async getPushEvents({
    type,
    filter,
  }: GetPushEventsParams): Promise<PushEvent[]> {
    const types = Array.isArray(type) ? type : [type];
    return this.pushEvents
      .where('type')
      .anyOf(types)
      .filter(filter ?? ((_) => true))
      .reverse()
      .sortBy('timestamp');
  }

  async addPushEvent(pushEvent: PushEvent) {
    await this.pushEvents.put(pushEvent, pushEvent.id);
  }

  async deletePushEvent(event?: PushEvent) {
    if (!event) {
      return;
    }
    await this.pushEvents.delete([event.id, event.timestamp]);
  }

  async deletePushEvents(events?: PushEvent[]) {
    if (!events) {
      return;
    }
    await this.pushEvents.bulkDelete(events.map((e) => [e.id, e.timestamp]));
  }

  async clearPushEvents() {
    await this.pushEvents.clear();
  }

  getTourPlanLoadFilterForDepotAndDate(
    depotId: string,
    date: Date,
  ): Promise<TourPlanLoadFilter | undefined> {
    return this.tourPlanLoadFilters
      .where(['depotId', 'date'])
      .equals([depotId, date])
      .first();
  }

  async putTourPlanLoadFilter(tourPlanLoadFilter: TourPlanLoadFilter) {
    await this.tourPlanLoadFilters.put(tourPlanLoadFilter);
  }

  async clearOutdatedTourPlanLoadFilters() {
    const maximumFilterAgeInDays = 2;
    const outdatedDateThreshold = new Date(Date.now());
    outdatedDateThreshold.setDate(
      outdatedDateThreshold.getDate() - maximumFilterAgeInDays,
    );

    await this.tourPlanLoadFilters
      .where('updatedAt')
      .belowOrEqual(outdatedDateThreshold)
      .delete();
  }
}
